import React, { useCallback } from 'react';
import { PhaseName } from '@/interfaces';
import useModal from './useModal';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import Icons from '@/const/icons';
import { Textarea } from '@/components/form';
import { Button } from '@/components/common';
import { ModalHeaderArrowBack } from '@/components/modals/ModalHeaderArrowBack';
import Images from '@/const/images';
import Image from 'next/image';
import { errorFieldMessage } from '@/const';

interface UseSendMessageToAllModalsProps {
	phaseName?: PhaseName;
	onSubmit: SubmitHandler<FieldValues>;
	selectedUserIds: (string | null)[];
	setSelectedUserIds: React.Dispatch<React.SetStateAction<(string | null)[]>>;
}

export const useSendMessageToAllModals = ({
	phaseName,
	onSubmit,
	selectedUserIds,
	setSelectedUserIds,
}: UseSendMessageToAllModalsProps) => {
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isValid },
	} = useForm();

	const rules = {
		message: {
			required: errorFieldMessage,
		},
	};

	const {
		Modal: ModalSendMessageToAll,
		show: showModalSendMessageToAll,
		hide: hideModalSendMessageToAll,
	} = useModal();

	const onOpenSendMessageToAll = useCallback(() => {
		reset();
		showModalSendMessageToAll();
	}, [reset, showModalSendMessageToAll]);

	const handleClose = () => {
		hideModalSendMessageToAll();
	};

	const renderSendMessageToAllModals = (
		<>
			<ModalSendMessageToAll
				title={
					<ModalHeaderArrowBack
						title="Send Message to All"
						handleClose={handleClose}
						showBack={false}
						iconTitleCustom={
							<Image
								alt="hat logo"
								src={Images.hatLogo}
								width={40}
								height={40}
							/>
						}
					/>
				}
				backgroundColor="white"
				classNameCardContainer="w-full md:!w-[720px]"
			>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col h-full"
				>
					<Textarea
						name="message"
						placeholder="Type your message here..."
						title="Type your update or announcement below:"
						register={register}
						rows={8}
						classNameContainerInput="!border-gray-disabled"
						error={errors.message}
						rules={rules.message}
					/>
					<div className="flex justify-end mt-8">
						<Button
							type="submit"
							size="fit"
							label="Send Message"
							decoration="primary"
							iconRight
							icon={Icons.sendEmail}
							disabled={!isValid}
						/>
					</div>
				</form>
			</ModalSendMessageToAll>
		</>
	);

	return {
		onOpenSendMessageToAll,
		renderSendMessageToAllModals,
		selectedUserIds,
		hideModalSendMessageToAll,
	};
};
